import type {
  ArithmeticWorksheet,
  LeveledWorksheetListItem,
  MyWorksheetListItem,
  PracticeTestWorksheetListItem,
  SharedCustomWorksheet,
  TextbookWorksheetListItem,
  Worksheet as WorksheetEntity,
  WorksheetFilterType,
  WorksheetListItem,
} from '@mathflat/domain/Worksheet'
import { format } from 'date-fns'
import { computed, makeObservable, observable } from 'mobx'
import { EMPTY_NUMBER_ID } from 'src/constant/common'

import commonRepo from '@/repository/common.repo'
import type { WorksheetSubTypeCode } from '@/types/learningContent'

import { DTO } from '../DTO'

/**
 * hwp 다운로드 가능 여부. 다운로드 모달은 Worksheet 인스턴스가 아닌 평면 객체를 받으므로
 * 메서드가 아닌 함수로 두어 양쪽이 같은 판정을 쓴다.
 */
export const canDownloadWorksheetHwp = (filterType?: WorksheetFilterType): boolean =>
  commonRepo.permission.byFeature.isAllowed('WORKSHEET_HWP_DOWNLOAD') &&
  filterType === 'MY_DB_ORIGINAL'

export class Worksheet extends DTO {
  id: WorksheetListItem['id'] = EMPTY_NUMBER_ID
  schoolType: WorksheetListItem['schoolType'] = 'ELEMENTARY'
  grade: WorksheetListItem['grade'] = '1'
  revision: WorksheetListItem['revision'] = 'CURRICULUM_15'
  title: WorksheetListItem['title'] = ''
  titleTag: WorksheetListItem['titleTag'] = ''
  tag?: WorksheetListItem['tag']
  chapter: WorksheetListItem['chapter'] = ''
  createDatetime?: Date
  problemCount: WorksheetListItem['problemCount'] = 0
  accessModifierToStudent: MyWorksheetListItem['accessModifierToStudent'] = 'PUBLIC'
  baseWorkbookType: MyWorksheetListItem['baseWorkbookType'] = null
  writer?:
    | MyWorksheetListItem['writer']
    | PracticeTestWorksheetListItem['writer']
    | SharedCustomWorksheet['writer']
  level?: LeveledWorksheetListItem['level'] | SharedCustomWorksheet['level']
  autoScorable?: WorksheetListItem['autoScorable']
  assignList?: WorksheetListItem['assignList']
  type?: ArithmeticWorksheet['type']
  thumbnail?: ArithmeticWorksheet['thumbnail']
  publisher?: TextbookWorksheetListItem['publisher']
  filterType?: MyWorksheetListItem['filterType']
  isArithmetic?: WorksheetEntity['isArithmetic']
  hwpDownloaded?: MyWorksheetListItem['hwpDownloaded']
  pendingSchedule?: MyWorksheetListItem['pendingSchedule']

  constructor(data) {
    super()

    if (data) {
      Object.assign(this, data)
    }

    if (data.createDatetime) {
      this.createDatetime = new Date(data.createDatetime)
    }

    makeObservable(this, {
      id: observable,
      schoolType: observable,
      grade: observable,
      title: observable,
      titleTag: observable,
      tag: observable,
      chapter: observable,
      createDatetime: observable,
      problemCount: observable,
      accessModifierToStudent: observable,
      writer: observable,
      level: observable,
      autoScorable: observable,
      assignList: observable,
      formatDate: computed,
      koreanLevel: computed,
      koSchoolType: computed,
      subjectGrade: computed,
      koLabel: computed,
      assignedStudentIdList: computed,
      assignedCount: computed,
      assignedStudentWorksheetList: computed,
      publisher: observable,
      baseWorkbookType: observable,
      hwpDownloaded: observable,
      pendingSchedule: observable,
    })
  }

  get formatDate() {
    if (!this.createDatetime) return null
    return format(this.createDatetime, 'yy.MM.dd')
  }

  get koreanLevel() {
    switch (this.level) {
      case 1:
        return '하'
      case 2:
        return '중하'
      case 3:
        return '중'
      case 4:
        return '상'
      case 5:
        return '최상'
      default:
        return '중'
    }
  }

  get koSchoolType() {
    switch (this.schoolType) {
      case 'ELEMENTARY':
        return '초등'
      case 'MIDDLE':
        return '중등'
      case 'HIGH':
        return '고등'
    }
  }

  // 내 학습지, 테마별 내신대비추천은 학년+과목을 사용한다.
  get subjectGrade() {
    if (this.schoolType === 'HIGH') {
      return this.grade
    }
    return this.grade + '학년'
  }

  // 테마별 학습지는 초1, 중2, 고3 과 같은 형식 사용한다.
  get koLabel() {
    let label = ''
    switch (this.schoolType) {
      case 'ELEMENTARY':
        label += '초'
        break
      case 'MIDDLE':
        label += '중'
        break
      case 'HIGH':
        label += '고'
    }
    return label + this.grade
  }

  get assignedStudentWorksheetList() {
    return this.assignList
      ?.filter(({ studentStatus }) => studentStatus === 'ACTIVE')
      .map(({ studentId, studentWorksheetId, studentName }) => ({
        studentId,
        studentDataRelationId: studentWorksheetId,
        studentName,
      }))
  }

  get assignedStudentIdList() {
    return this.assignList?.map(({ studentId }) => studentId)
  }

  get assignedCount() {
    return this.assignList?.length
  }

  canDownloadHwp(): boolean {
    return canDownloadWorksheetHwp(this.filterType)
  }

  static subTypeKr(
    subType: WorksheetSubTypeCode,
    options: {
      availableSubTypes: WorksheetSubTypeCode[]
      fallbackText?: string | null
    } = { availableSubTypes: [], fallbackText: null },
  ) {
    if (!options.availableSubTypes.includes(subType)) return options.fallbackText

    switch (subType) {
      case 12:
      case 16:
        return '챌린지'
      case 13:
        return '자기주도학습'
      case 14:
        return '오답보충학습'
      case 15:
        return '심화보충학습'
    }

    // 지원하는 케이스가 생길 때 추가
    return null
  }
}
